import { useEffect } from 'react';
import { useObjectStore } from '@stores/useObjectStore';
import { useUIStore, type WizardStep } from '@stores/useUIStore';
import { toast } from '@components/ui/Toast';

/**
 * Global keyboard shortcuts:
 *
 *   1 / 2 / 3             jump to wizard step (if reachable)
 *   Ctrl/Cmd + Z           undo
 *   Ctrl/Cmd + Shift + Z   redo
 *   Ctrl/Cmd + Y           redo (Windows alternative)
 *   Delete / Backspace     remove the selected object
 *   Escape                 deselect / dismiss modals
 *
 * Shortcuts are no-ops while focus is inside a text input / contentEditable.
 */
export function useKeyboardShortcuts(): void {
  useEffect(() => {
    function isTypingContext(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      if (target.isContentEditable) return true;
      const tag = target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    }

    function handler(e: KeyboardEvent) {
      if (isTypingContext(e.target)) return;
      const meta = e.ctrlKey || e.metaKey;
      const objStore = useObjectStore.getState();
      const uiStore = useUIStore.getState();

      // ── Step nav: 1/2/3 ──
      if (!meta && (e.key === '1' || e.key === '2' || e.key === '3')) {
        const step = Number(e.key) as WizardStep;
        if (step <= uiStore.highestStep) {
          uiStore.goToStep(step);
        }
        return;
      }

      // ── Undo / redo ──
      if (meta && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        objStore.undo();
        toast('Undo');
        return;
      }
      if (
        (meta && e.shiftKey && (e.key === 'z' || e.key === 'Z')) ||
        (meta && (e.key === 'y' || e.key === 'Y'))
      ) {
        e.preventDefault();
        objStore.redo();
        toast('Redo');
        return;
      }

      // ── Remove selected ──
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const uuid = objStore.selectedObjectUUID;
        if (uuid) {
          e.preventDefault();
          objStore.removeObject(uuid);
          toast('Object removed', 'warning');
        }
        return;
      }

      // ── Escape: deselect or dismiss ──
      if (e.key === 'Escape') {
        if (uiStore.isAboutVisible) {
          uiStore.dismissAbout();
        } else {
          objStore.deselectObject();
        }
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
}
